/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Input, Select, Space, Typography } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';

// Misc imports
import { LANGUAGE_OPTIONS } from '../../constants/main';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Constant declarations
const { Text } = Typography;

/**
 * A select component to choose the bot language. The language is set in the URL query parameters.
 * @returns {JSX.Element} The bot language select component.
 */
const BotLanguageSelect = ({
  tenantId, // If provided, will be used to override the tenantId from the URL
  size,
  selectStyle,
  getPopupContainer,
  disabled,
  hideButtonIcon,
  className,
  ...restProps
}) => {
  const { t } = useTranslation();
  const { botLanguages, currentBotLanguage, primaryBotLanguage, changeBotLanguage } = useBotLanguage(tenantId);

  return (
    <Input.Group className={`d-inline-flex align-items-center ${className}`} compact {...restProps}>
      {!hideButtonIcon ? (
        <Button
          type="custom"
          size={size}
          icon={<GlobalOutlined />}
          className="pointer-events-none"
          disabled={disabled}
        />
      ) : null}

      <Select
        value={currentBotLanguage}
        dropdownMatchSelectWidth={false}
        size={size}
        onSelect={(value) => changeBotLanguage(value)}
        suffixIcon={<ArrowDownIcon style={{ fontSize: 14 }} />}
        style={selectStyle}
        disabled={disabled}
        {...(getPopupContainer ? { getPopupContainer } : {})} // Only add getPopupContainer if it is defined, as it can interfere with the default behavior
        options={
          LANGUAGE_OPTIONS.filter(opt => botLanguages.includes(opt.key)).map(lang => ({
            value: lang.key,
            label: (
              <Space size={5}>
                {lang.label}
                {lang.key === primaryBotLanguage ? <Text type="secondary">{t('(primary)')}</Text> : null}
              </Space>
            )
          }))
        }
      />
    </Input.Group>
  );
};

BotLanguageSelect.propTypes = {
  tenantId: PropTypes.string,
  size: PropTypes.string,
  selectStyle: PropTypes.object,
  getPopupContainer: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  hideButtonIcon: PropTypes.bool
};

export default BotLanguageSelect;
